import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Pill, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ProductIngredients = ({ product }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'other'
    const [openAccordion, setOpenAccordion] = useState(null); // 'usage' or 'extra'

    // Safety checks for data
    const ingredientsActive = product?.ingredientsActive || [];
    const ingredientsOther = product?.ingredientsOther || "";
    const usage = product?.usage || "";
    const extraInfo = product?.extraInfo || "";

    // Helper to parse string ingredients if array is not provided
    const parsedActiveIngredients = Array.isArray(ingredientsActive)
        ? ingredientsActive
        : (typeof ingredientsActive === 'string' && ingredientsActive.length > 0)
            ? ingredientsActive.split('\n').map(line => {
                const parts = line.split(':');
                return { name: parts[0]?.trim(), amount: parts[1]?.trim() || "" };
            })
            : [];

    const toggleAccordion = (section) => {
        setOpenAccordion(openAccordion === section ? null : section);
    };

    return (
        <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">

            {/* Header */}
            <div className="px-6 pt-6 pb-2">
                <h2 className="text-xl font-bold text-fecos-text">{t("product.ingredients.title")}</h2>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 px-6 gap-6">
                <button
                    onClick={() => setActiveTab('active')}
                    className={`pb-3 text-sm font-semibold transition-all relative ${activeTab === 'active' ? 'text-fecos-accent' : 'text-gray-400 hover:text-gray-600'
                        }`}
                >
                    {t("product.ingredients.activeTab")}
                    {activeTab === 'active' && (
                        <motion.div layoutId="underline" className="absolute bottom-0 left-0 w-full h-[2px] bg-fecos-accent rounded-full" />
                    )}
                </button>

                <button
                    onClick={() => setActiveTab('other')}
                    className={`pb-3 text-sm font-semibold transition-all relative ${activeTab === 'other' ? 'text-fecos-accent' : 'text-gray-400 hover:text-gray-600'
                        }`}
                >
                    {t("product.ingredients.otherTab")}
                    {activeTab === 'other' && (
                        <motion.div layoutId="underline" className="absolute bottom-0 left-0 w-full h-[2px] bg-fecos-accent rounded-full" />
                    )}
                </button>
            </div>

            {/* Tab Content */}
            <div className="p-6 min-h-[150px]">
                <AnimatePresence mode="wait">
                    {activeTab === 'active' ? (
                        <motion.div
                            key="active"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.2 }}
                        >
                            {parsedActiveIngredients.length > 0 ? (
                                <ul className="space-y-3">
                                    {parsedActiveIngredients.map((ing, idx) => (
                                        <li key={idx} className="flex justify-between items-center text-sm py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 px-2 rounded-lg transition-colors">
                                            <span className="font-medium text-fecos-text">{ing.name}</span>
                                            <span className="font-bold text-fecos-accent">{ing.amount}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-400 italic">{t("product.ingredients.comingSoon")}</p>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="other"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.2 }}
                        >
                            {ingredientsOther ? (
                                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl">
                                    {ingredientsOther}
                                </p>
                            ) : (
                                <p className="text-sm text-gray-400 italic">{t("product.ingredients.comingSoon")}</p>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Accordions */}
            <div className="border-t border-gray-100">

                {/* Usage */}
                <div className="border-b border-gray-100 last:border-0">
                    <button
                        onClick={() => toggleAccordion('usage')}
                        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <Pill className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-semibold text-fecos-text">{t("product.ingredients.usage")}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openAccordion === 'usage' ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                        {openAccordion === 'usage' && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">
                                    {usage || t("product.ingredients.comingSoon")}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Extra Info */}
                <div className="border-b border-gray-100 last:border-0">
                    <button
                        onClick={() => toggleAccordion('extra')}
                        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-semibold text-fecos-text">{t("product.ingredients.extraInfo")}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${openAccordion === 'extra' ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                        {openAccordion === 'extra' && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">
                                    {extraInfo || t("product.ingredients.comingSoon")}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

            </div>
        </div>
    );
};

export default ProductIngredients;
